import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import { superAdminNavItems } from '../../constants/navigation';
import EmptyState from '../../components/Shared/EmptyState';

const PERIOD_OPTIONS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
];

const SuperAdminAnalyticsPage = () => {
  const { currentUser } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiRequest(`/api/super-admin/analytics?days=${period}`, 'GET');
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExportCSV = () => {
    if (!analytics) return;
    const rows = [['Metric', 'Value']];
    rows.push(['Total Schools', analytics.total_schools || 0]);
    rows.push(['Active Schools', analytics.active_schools || 0]);
    rows.push(['Total Students', analytics.total_students || 0]);
    rows.push(['Total Teachers', analytics.total_teachers || 0]);
    rows.push(['Total Sessions', analytics.total_sessions || 0]);
    if (analytics.dau_wau_mau) {
      rows.push(['DAU', analytics.dau_wau_mau.daily_active_users]);
      rows.push(['WAU', analytics.dau_wau_mau.weekly_active_users]);
      rows.push(['MAU', analytics.dau_wau_mau.monthly_active_users]);
    }
    if (analytics.school_comparison?.length) {
      rows.push([]);
      rows.push(['School Comparison']);
      rows.push(['School', 'Students', 'Teachers', 'Sessions', 'Avg Engagement']);
      analytics.school_comparison.forEach(s => {
        rows.push([s.school_name, s.student_count, s.teacher_count, s.sessions_count, s.avg_engagement]);
      });
    }
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform_analytics_${period}d_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout title="Platform Analytics" navItems={superAdminNavItems} userRole="super-admin" userName={currentUser?.email || 'Admin'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading analytics...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Platform Analytics" navItems={superAdminNavItems} userRole="super-admin" userName={currentUser?.email || 'Admin'}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">Error: {error}</div>
      </DashboardLayout>
    );
  }

  const dauWauMau = analytics?.dau_wau_mau || {};
  const schoolComparison = analytics?.school_comparison || [];
  const growthMetrics = analytics?.growth_metrics || {};
  const sessionsTrend = analytics?.sessions_trend || [];

  return (
    <DashboardLayout title="Platform Analytics" navItems={superAdminNavItems} userRole="super-admin" userName={currentUser?.email || 'Admin'}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-900">Platform Analytics</h2>
        <button onClick={handleExportCSV}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
          Export CSV
        </button>
      </div>

      {/* Period */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
        {PERIOD_OPTIONS.map(opt => (
          <button key={opt.value} onClick={() => setPeriod(opt.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === opt.value ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}>{opt.label}</button>
        ))}
      </div>

      {/* Empty State */}
      {!analytics?.total_schools && !analytics?.total_sessions && (
        <EmptyState
          icon="&#128200;"
          title="No Platform Data Yet"
          description="Data appears once schools are active and students start using Brillabee."
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard label="Total Schools" value={analytics?.total_schools || 0} />
        <SummaryCard label="Total Students" value={(analytics?.total_students || 0).toLocaleString()} />
        <SummaryCard label="Total Teachers" value={(analytics?.total_teachers || 0).toLocaleString()} />
        <SummaryCard label="Total Sessions" value={(analytics?.total_sessions || 0).toLocaleString()} />
      </div>

      {/* DAU / WAU / MAU */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <SummaryCard label="Daily Active Users" value={dauWauMau.daily_active_users || 0} color="purple" />
        <SummaryCard label="Weekly Active Users" value={dauWauMau.weekly_active_users || 0} color="purple" />
        <SummaryCard label="Monthly Active Users" value={dauWauMau.monthly_active_users || 0} color="purple" />
      </div>

      {/* Growth Metrics */}
      {growthMetrics.current_period && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Growth (vs previous {period} days)</h3>
          <div className="grid grid-cols-3 gap-4">
            <GrowthCard label="Students" growth={growthMetrics.students_growth} current={growthMetrics.current_period.students} previous={growthMetrics.previous_period.students} />
            <GrowthCard label="Teachers" growth={growthMetrics.teachers_growth} current={growthMetrics.current_period.teachers} previous={growthMetrics.previous_period.teachers} />
            <GrowthCard label="Schools" growth={growthMetrics.schools_growth} current={growthMetrics.current_period.schools} previous={growthMetrics.previous_period.schools} />
          </div>
        </div>
      )}

      {/* Sessions Trend */}
      {sessionsTrend.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sessions Trend</h3>
          <div className="flex items-end gap-0.5 h-40">
            {sessionsTrend.map((day, idx) => {
              const maxVal = Math.max(...sessionsTrend.map(d => d.count), 1);
              const height = (day.count / maxVal) * 100;
              return (
                <div key={idx} className="flex-1 group relative">
                  <div className="bg-purple-400 hover:bg-purple-600 rounded-t transition-all w-full"
                    style={{ height: `${Math.max(height, 2)}%` }} />
                  <div className="hidden group-hover:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                    {day.date}: {day.count} sessions
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>{sessionsTrend[0]?.date}</span>
            <span>{sessionsTrend[sessionsTrend.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* School Comparison */}
      {schoolComparison.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">School Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">School</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Students</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Teachers</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Sessions</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Avg Engagement</th>
                </tr>
              </thead>
              <tbody>
                {schoolComparison.map(s => (
                  <tr key={s.school_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium text-gray-900">{s.school_name}</td>
                    <td className="text-center py-2 px-3">{s.student_count}</td>
                    <td className="text-center py-2 px-3">{s.teacher_count}</td>
                    <td className="text-center py-2 px-3">{s.sessions_count}</td>
                    <td className="text-center py-2 px-3">
                      <span className={`font-semibold ${s.avg_engagement >= 60 ? 'text-green-600' : s.avg_engagement >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {s.avg_engagement}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const SummaryCard = ({ label, value, color = 'gray' }) => {
  const colorClass = color === 'purple' ? 'text-purple-600' : 'text-gray-900';
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-sm text-gray-600">{label}</p>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
    </div>
  );
};

const GrowthCard = ({ label, growth, current, previous }) => {
  const isPositive = growth > 0;
  const isZero = growth === 0;
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <p className="text-sm text-gray-600">{label}</p>
      <p className={`text-xl font-bold ${isPositive ? 'text-green-600' : isZero ? 'text-gray-600' : 'text-red-600'}`}>
        {isPositive ? '+' : ''}{growth}%
      </p>
      <p className="text-xs text-gray-500 mt-1">{current} this period vs {previous} last</p>
    </div>
  );
};

export default SuperAdminAnalyticsPage;
